
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import AccessCredentials from "@/components/AccessCredentials";
import { AlertTriangle, CheckCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useState, useEffect } from "react";

interface ApiUsageStats {
  total: number;
  text: number;
  image: number;
  document: number;
  video: number;
  voice: number;
  limit: number;
}

const ApiCredentials = () => {
  const { user, isLoading } = useAuth();
  const [apiUsage, setApiUsage] = useState<ApiUsageStats>({
    total: 0,
    text: 0,
    image: 0,
    document: 0,
    video: 0,
    voice: 0,
    limit: 1000,
  });

  useEffect(() => {
    // In production, this would fetch actual API usage from the backend
    // For now, we'll simulate usage statistics
    const simulateApiUsage = () => {
      setApiUsage({
        total: 423,
        text: 187,
        image: 92,
        document: 65,
        video: 43,
        voice: 36,
        limit: 1000,
      });
    };

    simulateApiUsage();
  }, []);

  if (isLoading) {
    return <div className="flex h-screen items-center justify-center">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  const usagePercentage = (apiUsage.total / apiUsage.limit) * 100;
  const isLowUsage = usagePercentage < 70;

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold tracking-tight">API Credentials</h1>
        <p className="text-muted-foreground">
          Manage your API keys and access credentials for integrating with FraudEye services.
        </p>
      </div>

      <Alert variant="warning" className="mb-6">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Keep your credentials secure</AlertTitle>
        <AlertDescription>
          Your API keys grant access to your FraudEye account. Never share them in public repositories
          or client-side code.
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <AccessCredentials />
          
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>API Usage</CardTitle>
              <CardDescription>
                Your current API usage statistics
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm font-medium">Total Usage</span>
                  <span className="text-sm text-muted-foreground">{apiUsage.total} / {apiUsage.limit}</span>
                </div>
                <Progress value={usagePercentage} className={`h-2 ${isLowUsage ? "bg-green-500" : "bg-yellow-500"}`} />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <span className="text-sm font-medium">Text Analysis</span>
                  <Progress value={(apiUsage.text / apiUsage.total) * 100} className="h-1.5 bg-blue-500" />
                  <span className="text-xs text-muted-foreground">{apiUsage.text} calls</span>
                </div>
                <div className="space-y-1">
                  <span className="text-sm font-medium">Image Analysis</span>
                  <Progress value={(apiUsage.image / apiUsage.total) * 100} className="h-1.5 bg-purple-500" />
                  <span className="text-xs text-muted-foreground">{apiUsage.image} calls</span>
                </div>
                <div className="space-y-1">
                  <span className="text-sm font-medium">Document Analysis</span>
                  <Progress value={(apiUsage.document / apiUsage.total) * 100} className="h-1.5 bg-amber-500" />
                  <span className="text-xs text-muted-foreground">{apiUsage.document} calls</span>
                </div>
                <div className="space-y-1">
                  <span className="text-sm font-medium">Video Analysis</span>
                  <Progress value={(apiUsage.video / apiUsage.total) * 100} className="h-1.5 bg-red-500" />
                  <span className="text-xs text-muted-foreground">{apiUsage.video} calls</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div>
          <Card>
            <CardHeader>
              <CardTitle>API Status</CardTitle>
              <CardDescription>
                Current system status and reliability
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Alert variant="success" className="mb-4">
                <CheckCircle className="h-4 w-4" />
                <AlertTitle>All systems operational</AlertTitle>
                <AlertDescription>
                  All fraud detection services are running optimally.
                </AlertDescription>
              </Alert>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center border-b pb-2">
                  <span className="font-medium">Text Analysis API</span>
                  <span className="text-sm text-green-600 bg-green-100 px-2 py-0.5 rounded-full">Operational</span>
                </div>
                <div className="flex justify-between items-center border-b pb-2">
                  <span className="font-medium">Image Analysis API</span>
                  <span className="text-sm text-green-600 bg-green-100 px-2 py-0.5 rounded-full">Operational</span>
                </div>
                <div className="flex justify-between items-center border-b pb-2">
                  <span className="font-medium">Document Analysis API</span>
                  <span className="text-sm text-green-600 bg-green-100 px-2 py-0.5 rounded-full">Operational</span>
                </div>
                <div className="flex justify-between items-center border-b pb-2">
                  <span className="font-medium">Video Analysis API</span>
                  <span className="text-sm text-green-600 bg-green-100 px-2 py-0.5 rounded-full">Operational</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium">Voice Analysis API</span>
                  <span className="text-sm text-green-600 bg-green-100 px-2 py-0.5 rounded-full">Operational</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="mt-6">
            <h2 className="text-xl font-semibold mb-4">Usage Instructions</h2>
            <div className="bg-muted p-4 rounded-md mb-4">
              <pre className="text-sm overflow-auto"><code>{`
# Example API Request
curl -X POST \\
  https://api.fraudeye.app/v1/analyze \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"url": "https://example.com/image.jpg"}'
              `}</code></pre>
            </div>
            <p className="text-sm text-muted-foreground">
              For detailed API documentation and SDK examples, please visit our
              <Button variant="link" className="px-1 h-auto" asChild>
                <a href="/documentation">developer documentation</a>
              </Button>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiCredentials;
